import {
  buildIndexedArray,
  isTreeType,
  PublicProjectSummary,
  ReferralImpact,
  Tile,
  User,
  isSubProfileType,
  SubProfileTileType,
  getImpactInstance,
  isCarbonRemovalType,
  isHabitatRestorationType,
  isCarbonOffsetType,
  Impact,
  isTree,
  TileSource,
  TileVariant,
} from "@ecologi/shared";
import styled from "@emotion/styled";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import useMeasure from "react-use/lib/useMeasure";
import useWindowSize from "react-use/lib/useWindowSize";
import { Button } from "../Button";
import { Typography } from "../Typography";
import { ShowOlderButton } from "./ShowOlderButton";
import { getNumberOfTilesPerPage } from "../OmniForest/utils/get-number-of-tiles-per-page";
import { useEscape } from "../utils/use-escape";
import { scrollToTileById } from "../OmniForest/utils/scroll-to-tile-by-id";
import {
  ForestSubprofileTile,
  ForestTileImpact,
  ForestTileInvestor,
  ForestTileReferral,
  ForestTileTree,
} from "..";
import { useRouter } from "next/router";
import { ForestImpactModal } from "../ForestModals/ForestImpactModal";
import { ForestTreeModal } from "../ForestModals/ForestTreeModal";

export const Plot = styled.div`
  margin: 48px auto 0;
  padding: 0;

  @media ${(props) => props.theme.bp.mobileUp} {
    padding: 0 50px;
  }
`;

const InMiddleOfForestText = styled(Typography)`
  opacity: 0.5;
  margin: 0px 0 64px 0;
  text-align: center;
  color: ${(props) => props.theme.colors.snowdrop} !important;
  position: relative;
  z-index: 9;
  a {
    text-decoration: underline;
    &:hover {
      cursor: pointer;
    }
  }
`;

export const PlotWrapper = styled.ul`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 0;
  padding: 0;
`;

export const ShowPrevPageButton = styled(Button)`
  margin-bottom: 24px;
`;

const Spacer = styled.li`
  height: 50px;
  margin: 0 5px;
  width: 100px;

  @media ${(props) => props.theme.bp.tabletUp} {
    height: 82px;
    margin: 0 10px;
    width: 164px;
  }
`;

const EmptyMessage = styled.div`
  margin-top: 6rem;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 5px;
  display: inline-block;
  padding: 20px;
  position: relative;
  top: -20px;
`;

export type ForestTilesListProps = {
  allProjects: PublicProjectSummary[];
  asPath?: string;
  getOlderTiles: () => void;
  goBackToTop: () => void;
  hasMoreNewer: boolean;
  hasMoreOlder: boolean;
  isOwnProfile: boolean;
  profile: User;
  referralData?: ReferralImpact;
  tileIdToView?: string;
  tiles: Tile[];
  user: User | null;
};

export function ForestTilesList({
  allProjects,
  asPath,
  getOlderTiles,
  goBackToTop,
  hasMoreNewer,
  hasMoreOlder,
  isOwnProfile,
  profile,
  referralData,
  tileIdToView,
  tiles,
  user,
}: ForestTilesListProps) {
  const router = useRouter();

  const [selectedProject, setSelectedProject] =
    useState<PublicProjectSummary>();
  const [selectedTile, setSelectedTile] = useState<{
    tile: Tile;
    impact: Impact;
    isMocked: boolean;
  }>();

  const selectTile = useCallback(
    (tileId: string | undefined, projectId: number | undefined) => {
      const project = allProjects.find((p) => p.id === projectId);

      setSelectedProject(project);

      router.push(
        {
          pathname: profile.getProfilePath(),
          query: tileId ? { tileId } : {},
        },
        undefined,
        { shallow: true }
      );
    },
    [allProjects, router]
  );

  const onCloseTile = () => selectTile(undefined, undefined);

  const { width: windowWidth } = useWindowSize();
  const [forestPlotRef, { width: forestWidth }] = useMeasure();
  useEscape(() => {
    setSelectedTile(undefined);
    onCloseTile();
  });
  const forestTilesPerPage = getNumberOfTilesPerPage(windowWidth);
  const referralTrees = referralData?.trees || [];

  const handleTileClick = (tile: Tile, project?: PublicProjectSummary) => {
    setSelectedTile({
      tile,
      impact: getImpactInstance(tile, project),
      isMocked: "isMocked" in tile && Boolean(tile.isMocked),
    });
    selectTile(tile._id, project?.id);
  };

  const handleTileAddedToForest = () => {
    isNudged = tileCounter === numForestColumns ? !isNudged : isNudged;
    tileCounter = tileCounter === numForestColumns ? 1 : tileCounter + 1;
  };

  const referralOrSubForestTiles: SubProfileTileType[] = useMemo(
    () =>
      referralTrees.map((t, idx) => ({
        _id: `${t?.userId}`,
        username: t.isPrivate ? "Private user" : t.username || t.groupName,
        name: t.isPrivate ? "Private user" : t.username || t.groupName,
        firstName: "",
        lastName: "",
        style: idx % 2 === 0 ? "a" : "b",
        isPrivate: t.isPrivate == true,
        totalTrees: t.totalTrees,
        userId: t.userId,
        projectTreeCounts: t.projectTreeCounts,
        createdAt: t.createdAt,
        source: TileSource.ECOLOGI_FEATURE,
        variant: TileVariant.SUBPROFILE,
      })),
    [referralTrees]
  );

  // Referral "child" tiles should display at start of forest
  const combinedTiles: Tile[] = useMemo(
    () => [...referralOrSubForestTiles, ...tiles],
    [referralOrSubForestTiles, tiles]
  );

  const isReferralTileVisible = isOwnProfile;
  // Because we have invite friends tile, and also investor tile
  // so we should + 1 for each when its value is enabled
  const numTiles =
    combinedTiles.length +
    (isReferralTileVisible ? 1 : 0) +
    (profile.isInvestor ? 1 : 0);

  // Calculate the number of columns for forest
  const numForestColumns = Math.floor(
    forestWidth / (windowWidth > 767 ? 184 : 110)
  );
  // We pad the forest to make sure the last items are left aligned
  // when using flex's justify-content: center;

  let numberOfHiddenCells =
    numForestColumns > numTiles
      ? 0
      : numForestColumns - (numTiles % numForestColumns);

  /** Offsets tiles slightly to the left/right (true/false) to create the alternating pattern */
  let isNudged = false;
  /** The current position of the tile across a row */
  let tileCounter = 0;

  if (isReferralTileVisible) {
    tileCounter++;
  }

  if (profile.isInvestor) {
    tileCounter++;
  }

  useEffect(() => {
    if (!asPath?.includes("tileId")) setSelectedTile(undefined);
  }, [asPath]);

  const isForestEmpty = !combinedTiles.length;
  const totalTileSum = combinedTiles.length + tileCounter;

  useEffect(() => {
    const isSubProfileTile = referralOrSubForestTiles.some(
      (tile) => tile._id === tileIdToView
    );

    if (isSubProfileTile) scrollToTileById(tileIdToView);
  }, [referralOrSubForestTiles.length]);

  return (
    <>
      <Plot ref={forestPlotRef as any}>
        {hasMoreNewer && (
          <InMiddleOfForestText use="body1" as="p">
            You're in the middle of the forest! Take me to the{" "}
            <a onClick={goBackToTop}>most recently funded projects.</a>
          </InMiddleOfForestText>
        )}
        {isForestEmpty ? (
          <PlotWrapper data-testid="forest-tiles-empty">
            <ForestTileReferral
              profile={profile}
              user={user}
              isForestEmpty={!profile.isInvestor}
            />
            {profile.isInvestor && <ForestTileInvestor />}
          </PlotWrapper>
        ) : (
          <PlotWrapper data-testid="forest-tiles-filled">
            {isReferralTileVisible && (
              <ForestTileReferral
                profile={profile}
                user={user}
                isForestEmpty={combinedTiles.length === 0}
              />
            )}

            {profile.isInvestor && <ForestTileInvestor />}

            {combinedTiles.map((tile: Tile, idx) => {
              const key = "_id" in tile ? tile._id : idx;

              const isLinkedTile =
                !!tileIdToView && !selectedTile && tile._id === tileIdToView;

              if (isSubProfileType(tile)) {
                handleTileAddedToForest();
                return (
                  <ForestSubprofileTile
                    key={key}
                    isLinkedTile={isLinkedTile}
                    isNudged={isNudged}
                    numbering={totalTileSum}
                    onClick={() => handleTileClick(tile)}
                    tree={getImpactInstance(tile)}
                  />
                );
              }

              // Impact tiles have projects
              const project = allProjects.find((p) => p.id === tile.projectId);

              if (
                isCarbonOffsetType(tile, project) ||
                isCarbonRemovalType(tile, project) ||
                isHabitatRestorationType(tile)
              ) {
                handleTileAddedToForest();
                return (
                  <ForestTileImpact
                    key={key}
                    impact={getImpactInstance(tile, project)}
                    isLinkedTile={isLinkedTile}
                    isNudged={isNudged}
                    numbering={totalTileSum}
                    onClick={() => handleTileClick(tile, project)}
                  />
                );
              }

              if (isTreeType(tile)) {
                handleTileAddedToForest();
                return (
                  <ForestTileTree
                    key={key}
                    isLinkedTile={isLinkedTile}
                    isNudged={isNudged}
                    numbering={totalTileSum}
                    onClick={() => handleTileClick(tile, project)}
                    profile={profile}
                    tree={getImpactInstance(tile, project)}
                  />
                );
              }

              // If no tile was added to forest, compensate with hidden cells
              numberOfHiddenCells += 1;

              return null;
            })}

            {buildIndexedArray(numberOfHiddenCells % numForestColumns).map(
              (idx: number) => (
                <Spacer key={`hidden-${idx}`} />
              )
            )}
          </PlotWrapper>
        )}

        {combinedTiles.length === 0 && (
          <EmptyMessage>
            No impact in {isOwnProfile ? "your" : profile.fullNamePlural} forest
            yet
          </EmptyMessage>
        )}
      </Plot>
      {tiles.length >= forestTilesPerPage && (
        <ShowOlderButton
          onClick={getOlderTiles}
          canGetMore={hasMoreOlder}
          totalTiles={tiles.length}
        />
      )}

      {selectedTile && isTree(selectedTile.impact) && (
        <ForestTreeModal
          closePopup={() => {
            setSelectedTile(undefined);
            onCloseTile();
          }}
          isOpen={!!selectedTile}
          isOwnProfile={isOwnProfile}
          project={selectedProject}
          tileInfo={selectedTile}
          user={profile}
        />
      )}

      {selectedTile && selectedProject && !isTree(selectedTile.impact) && (
        <ForestImpactModal
          closePopup={() => {
            setSelectedTile(undefined);
            onCloseTile();
          }}
          isOpen={!!selectedTile}
          isOwnProfile={isOwnProfile}
          project={selectedProject}
          tileInfo={selectedTile}
          user={profile}
        />
      )}
    </>
  );
}
